import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const inputClass =
  "mt-1 block w-full rounded-md bg-gray-900 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
const labelClass =
  "block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1";
const groupLabelClass =
  "text-[11px] font-bold text-orange-400/80 uppercase tracking-wider mb-3 flex items-center gap-2";

const UNION_FIELDS = [
  {
    group: "Rates",
    fields: [
      {
        name: "pieceworkerBurden",
        label: "Burden",
        suffix: "%",
        step: "0.1",
        max: "100",
      },
      { name: "vacationPay", label: "Vacation", suffix: "%/hr", step: "0.01" },
    ],
  },
  {
    group: "Fixed Costs ($/hr)",
    fields: [
      { name: "pension", label: "Pension", suffix: "$", step: "0.01" },
      {
        name: "healthWelfare",
        label: "Health & Welfare",
        suffix: "$",
        step: "0.01",
      },
      { name: "funds", label: "Funds", suffix: "$", step: "0.01" },
      { name: "sub", label: "Sub", suffix: "$", step: "0.01" },
    ],
  },
];

const ALL_FIELD_NAMES = UNION_FIELDS.flatMap((g) =>
  g.fields.map((f) => f.name),
);

const emptyUnion = {
  title: "",
  pieceworkerBurden: "",
  vacationPay: "",
  pension: "",
  healthWelfare: "",
  funds: "",
  sub: "",
  duesAmount: "", // dollar figure only — deduction method (hours vs flat
  // dollars) is chosen later, per-charge, from the payroll report.
};

// Dues is optional — a blank/zero duesAmount just means this union can't
// have dues charged against it (enforced backend-side too), so it doesn't
// gate the rest of the form's validity.
const isDuesValid = (u) =>
  u.duesAmount === "" ||
  (!isNaN(parseFloat(u.duesAmount)) && parseFloat(u.duesAmount) >= 0);

const isValid = (u) =>
  u &&
  u.title?.trim() &&
  ALL_FIELD_NAMES.every(
    (name) =>
      u[name] !== "" && !isNaN(parseFloat(u[name])) && parseFloat(u[name]) >= 0,
  ) &&
  isDuesValid(u);

const API = "https://erp-xe9u.onrender.com/api";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const fmt = (v) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
  }).format(v);

// Defined outside component so it never gets recreated — avoids the ref warning
function UnionForm({ data, onChange, onSave, onCancel, saveLabel }) {
  const duesAmountNum = parseFloat(data.duesAmount);
  const hasDues =
    data.duesAmount !== "" && !isNaN(duesAmountNum) && duesAmountNum > 0;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-5 shadow-lg">
      {/* Title — set apart as the identity of the record */}
      <div className="mb-6 pb-5 border-b border-gray-700">
        <label className={labelClass}>Union Title</label>
        <input
          type="text"
          name="title"
          value={data.title}
          onChange={onChange}
          placeholder="e.g. LMCA"
          className={`${inputClass} max-w-xs text-base font-medium`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {UNION_FIELDS.map(({ group, fields }) => (
          <div key={group}>
            <div className={groupLabelClass}>
              <span className="h-px flex-1 bg-orange-400/20" />
              {group}
              <span className="h-px flex-1 bg-orange-400/20" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fields.map(({ name, label, suffix, step, max }) => (
                <div key={name} className="relative">
                  <label className={labelClass}>{label}</label>
                  <input
                    type="number"
                    name={name}
                    value={data[name]}
                    onChange={onChange}
                    step={step}
                    min="0"
                    max={max}
                    placeholder="0.00"
                    className={`${inputClass} pr-8`}
                  />
                  <span className="pointer-events-none absolute right-2.5 top-[29px] text-xs text-gray-500">
                    {suffix}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Dues — separated out since it behaves differently (optional, and
          the deduction method isn't chosen here) */}
      <div className="mt-6 pt-5 border-t border-gray-700">
        <div className={groupLabelClass}>
          <span className="h-px flex-1 bg-orange-400/20" />
          Union Dues
          <span className="h-px flex-1 bg-orange-400/20" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-full sm:w-48 shrink-0">
            <label className={labelClass}>Dues Amount ($)</label>
            <input
              type="number"
              name="duesAmount"
              value={data.duesAmount}
              onChange={onChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className={inputClass}
            />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed pt-1 sm:pt-6">
            {hasDues ? (
              <>
                <span className="text-gray-300 font-medium">
                  {fmt(duesAmountNum)}
                </span>{" "}
                will be available to deduct from each worker in this union.
                Whether it's taken as a flat dollar amount or converted to
                rounded hours is chosen when charging dues from the payroll
                report.
              </>
            ) : (
              "Leave at $0 to disable union dues charging for this union."
            )}
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-gray-700">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={!isValid(data)}
          className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

export default function UnionManagement({
  unions,
  setUnions,
  setDialog,
  isAddingUnion,
  setIsAddingUnion,
  currentUnionPage,
  setCurrentUnionPage,
}) {
  const [isEditingUnion, setIsEditingUnion] = useState(false);
  const [editingUnion, setEditingUnion] = useState(null);
  const [newUnion, setNewUnion] = useState(emptyUnion);
  const [unionsPerPage] = useState(10);
  const [expandedId, setExpandedId] = useState(null);
  const [tplByUnion, setTplByUnion] = useState({}); // title → [{kind, source, ...}]

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll/union-dues/templates`, {
        headers: authHeaders(),
      });
      const map = {};
      (res.data.unions || []).forEach((u) => (map[u.union] = u.templates));
      setTplByUnion(map);
    } catch {
      /* templates are optional here — the list still renders */
    }
  }, []);

  const unionTitlesKey = unions.map((u) => u.title).join("|");
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates, unionTitlesKey]);

  const handleNewChange = (e) =>
    setNewUnion((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleEditChange = (e) =>
    setEditingUnion((p) => ({ ...p, [e.target.name]: e.target.value }));

  const buildPayload = (u) => ({
    title: u.title,
    pieceworkerBurden: parseFloat(u.pieceworkerBurden),
    vacationPay: parseFloat(u.vacationPay).toFixed(2),
    pension: parseFloat(u.pension).toFixed(2),
    healthWelfare: parseFloat(u.healthWelfare).toFixed(2),
    funds: parseFloat(u.funds).toFixed(2),
    sub: parseFloat(u.sub).toFixed(2),
    duesAmount: u.duesAmount === "" ? 0 : parseFloat(u.duesAmount).toFixed(2),
  });

  const handleAddUnion = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions",
        buildPayload(newUnion),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUnions(response.data);
      setNewUnion(emptyUnion);
      setIsAddingUnion(false);
      setCurrentUnionPage(1);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Union added successfully!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to add union.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleEditUnion = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions/${editingUnion._id}`,
        buildPayload(editingUnion),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUnions(response.data);
      setIsEditingUnion(false);
      setEditingUnion(null);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Union updated!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to update union.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleDeleteUnion = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUnions(unions.filter((u) => u._id !== id));
      setCurrentUnionPage(1);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Union deleted.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to delete union.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleStartEdit = (u) => {
    setEditingUnion({
      ...u,
      pieceworkerBurden: u.pieceworkerBurden.toString(),
      vacationPay: u.vacationPay.toString(),
      pension: u.pension.toString(),
      healthWelfare: u.healthWelfare.toString(),
      funds: u.funds.toString(),
      sub: u.sub.toString(),
      duesAmount: (u.duesAmount ?? 0).toString(),
    });
    setIsEditingUnion(true);
    setExpandedId(null);
  };

  const totalPages = Math.ceil(unions.length / unionsPerPage);
  const start = (currentUnionPage - 1) * unionsPerPage;
  const paginated = unions.slice(start, start + unionsPerPage);

  return (
    <div>
      {/* Add form */}
      {isAddingUnion && (
        <UnionForm
          data={newUnion}
          onChange={handleNewChange}
          onSave={handleAddUnion}
          onCancel={() => {
            setIsAddingUnion(false);
            setNewUnion(emptyUnion);
          }}
          saveLabel="Add Union"
        />
      )}

      {/* Edit form */}
      {isEditingUnion && editingUnion && (
        <UnionForm
          data={editingUnion}
          onChange={handleEditChange}
          onSave={handleEditUnion}
          onCancel={() => {
            setIsEditingUnion(false);
            setEditingUnion(null);
          }}
          saveLabel="Save Changes"
        />
      )}

      {/* Card list — replaces the old 9-column table. Each union is a row
          with only the identity + dues status visible; everything else
          expands on click. This avoids the old table's problem of hiding
          4 of 8 columns below xl breakpoints. */}
      {paginated.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-16 text-center">
          <p className="text-sm text-gray-500">No unions added yet.</p>
          <p className="text-xs text-gray-600 mt-1">
            Add one to start configuring rates and dues.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {paginated.map((u) => {
            const isOpen = expandedId === u._id;
            const hasDues = u.duesAmount > 0;
            return (
              <div
                key={u._id}
                className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden transition-colors hover:border-gray-600"
              >
                {/* Summary row — always visible */}
                <button
                  onClick={() => setExpandedId(isOpen ? null : u._id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left"
                >
                  <svg
                    className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-100">
                      {u.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {u.pieceworkerBurden.toFixed(1)}% burden ·{" "}
                      {fmt(u.pension)} pension/hr
                    </p>
                  </div>

                  {/* Template status — missing templates block remitting */}
                  {(tplByUnion[u.title] || []).some(
                    (t) => t.source === "missing",
                  ) && (
                    <span className="hidden sm:inline-flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-400/10 border border-amber-400/20 text-xs font-medium text-amber-300">
                      Templates needed
                    </span>
                  )}

                  {/* Dues badge — the one piece of info worth surfacing
                      at a glance without expanding */}
                  <div className="shrink-0 text-right">
                    {hasDues ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-400/10 border border-orange-400/20 text-xs font-medium text-orange-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                        {fmt(u.duesAmount)} dues
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-700/50 border border-gray-700 text-xs font-medium text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                        No dues
                      </span>
                    )}
                  </div>

                  <div
                    className="flex items-center gap-1.5 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => handleStartEdit(u)}
                      onKeyDown={(e) => e.key === "Enter" && handleStartEdit(u)}
                      className="px-2.5 py-1.5 text-xs font-medium text-orange-300 hover:text-white bg-orange-400/10 hover:bg-orange-400/20 rounded-md transition-colors border border-orange-400/20 cursor-pointer"
                    >
                      Edit
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => handleDeleteUnion(u._id)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleDeleteUnion(u._id)
                      }
                      className="px-2.5 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-colors border border-red-400/20 hover:border-red-500 cursor-pointer"
                    >
                      Delete
                    </span>
                  </div>
                </button>

                {/* Expanded detail — full rate breakdown */}
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-gray-700/60">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
                      <DetailStat
                        label="Burden"
                        value={`${u.pieceworkerBurden.toFixed(1)}%`}
                      />
                      <DetailStat
                        label="Vacation"
                        value={`${u.vacationPay}%/hr`}
                      />
                      <DetailStat
                        label="Pension"
                        value={`${fmt(u.pension)}/hr`}
                      />
                      <DetailStat
                        label="Health & Welfare"
                        value={`${fmt(u.healthWelfare)}/hr`}
                      />
                      <DetailStat label="Funds" value={`${fmt(u.funds)}/hr`} />
                      <DetailStat label="Sub" value={`${fmt(u.sub)}/hr`} />
                    </div>
                    <UnionTemplates
                      union={u.title}
                      templates={tplByUnion[u.title]}
                      onChanged={loadTemplates}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Page {currentUnionPage} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentUnionPage((p) => Math.max(1, p - 1))}
              disabled={currentUnionPage === 1}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const s = Math.max(
                1,
                Math.min(currentUnionPage - 2, totalPages - 4),
              );
              const page = s + i;
              return page <= totalPages ? (
                <button
                  key={page}
                  onClick={() => setCurrentUnionPage(page)}
                  className={`w-8 h-8 text-xs font-medium rounded-md transition-colors ${
                    currentUnionPage === page
                      ? "bg-orange-400 text-white border border-orange-400"
                      : "border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  {page}
                </button>
              ) : null;
            })}
            <button
              onClick={() =>
                setCurrentUnionPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentUnionPage === totalPages}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailStat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
        {label}
      </p>
      <p className="text-sm font-medium text-gray-200">{value}</p>
    </div>
  );
}

// Remittance workbook templates for one union. Every union has an hourly (ICI)
// and a piecework template; uploads are checked against that layout before
// they're accepted. Reporting columns must stay the same year to year — the
// calculation sheets can change freely.
function UnionTemplates({ union, templates, onChanged }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const run = async (kind, fn, okText) => {
    setBusy(kind);
    setMsg(null);
    try {
      await fn();
      setMsg({ type: "ok", text: okText });
      await onChanged();
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.message,
        errors: err.response?.data?.errors,
      });
    } finally {
      setBusy("");
    }
  };

  const upload = (kind, label, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("union", union);
    fd.append("kind", kind);
    fd.append("file", file);
    return run(
      kind,
      () =>
        axios.post(`${API}/payroll/union-dues/templates`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        }),
      `${file.name} is now the ${label} template for ${union}.`,
    );
  };

  const applyBuiltin = (kind, label) =>
    run(
      kind,
      () =>
        axios.post(
          `${API}/payroll/union-dues/templates/use-builtin`,
          { union, kind },
          { headers: authHeaders() },
        ),
      `Built-in DALI ${label} template applied.`,
    );

  const remove = (kind, label) =>
    run(
      kind,
      () =>
        axios.delete(`${API}/payroll/union-dues/templates`, {
          headers: authHeaders(),
          params: { union, kind },
        }),
      `Removed the ${label} template.`,
    );

  const download = async (t) => {
    try {
      const res = await axios.get(
        `${API}/payroll/union-dues/templates/download`,
        {
          headers: authHeaders(),
          responseType: "blob",
          params: { union, kind: t.kind },
        },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = t.filename || `${union}_${t.kind}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.message,
      });
    }
  };

  const rows =
    templates && templates.length
      ? templates
      : [
          { kind: "hourly", label: "Hourly (ICI)", source: "missing" },
          { kind: "piecework", label: "Piecework", source: "missing" },
        ];

  return (
    <div className="mt-5 pt-4 border-t border-gray-700/60">
      <div className={groupLabelClass}>
        <span className="h-px flex-1 bg-orange-400/20" />
        Union Dues Remittance Templates
        <span className="h-px flex-1 bg-orange-400/20" />
      </div>
      <p className="text-xs text-gray-500 mb-3">
        The Excel workbooks filled in when remitting dues to {union}. Upload the
        new file each year — the reporting columns must stay the same, the
        calculation sheets can change.
      </p>
      <div className="space-y-2">
        {rows.map((t) => {
          const label = t.kind === "hourly" ? "hourly (ICI)" : "piecework";
          const isBusy = busy === t.kind;
          return (
            <div
              key={t.kind}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3"
            >
              <span className="text-sm font-medium text-gray-200 w-28 shrink-0">
                {t.label}
              </span>
              <span
                className={`flex-1 min-w-[180px] text-xs ${
                  t.source === "missing" ? "text-amber-400" : "text-gray-400"
                }`}
              >
                {t.source === "uploaded"
                  ? `${t.filename} · uploaded ${fmtDate(t.uploadedAt)}`
                  : t.source === "builtin"
                    ? "Built-in DALI template"
                    : "No template yet — remitting to this union is blocked"}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <label
                  className={`px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md cursor-pointer transition-colors ${
                    isBusy ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  {isBusy
                    ? "Working…"
                    : t.source === "uploaded"
                      ? "Replace"
                      : "Upload"}
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => {
                      upload(t.kind, label, e.target.files[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
                {t.source !== "missing" && (
                  <button
                    onClick={() => download(t)}
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600 transition-colors"
                  >
                    Download
                  </button>
                )}
                {t.source !== "uploaded" && (
                  <button
                    onClick={() => applyBuiltin(t.kind, label)}
                    disabled={isBusy}
                    title="Copy the built-in DALI layout into this union"
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600 transition-colors disabled:opacity-40"
                  >
                    Use built-in DALI
                  </button>
                )}
                {t.source === "uploaded" && (
                  <button
                    onClick={() => remove(t.kind, label)}
                    disabled={isBusy}
                    className="px-2.5 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md border border-red-400/20 transition-colors disabled:opacity-40"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {msg && (
        <div
          className={`mt-3 px-3 py-2 rounded-md text-xs border ${
            msg.type === "ok"
              ? "text-green-300 bg-green-500/10 border-green-500/20"
              : "text-red-300 bg-red-500/10 border-red-500/20"
          }`}
        >
          {msg.text}
          {msg.errors?.length > 0 && (
            <ul className="list-disc ml-4 mt-1">
              {msg.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
